import { FlatTreeControl } from '@angular/cdk/tree';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Folder } from '@api/types';
import { DragDropRegion, DragDropService } from '@shared/services/drag-drop.service';
import { SidebarService } from '@shared/services/sidebar.service';
import { Subscription } from 'rxjs';
import { FolderMenuDataSource, FolderNode } from './folder-menu.datasource';

@Component({
  selector: 'rav-folder-menu',
  templateUrl: './folder-menu.component.html',
  styleUrls: ['./folder-menu.component.scss']
})
export class FolderMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isFolderSelect = false;
  @Input() addInboxNode = false;
  @Input() foldersToDisable: string[] = [];
  @Output() folderSelected: EventEmitter<FolderNode> = new EventEmitter();

  DragDropRegion = DragDropRegion;

  treeControl: FlatTreeControl<FolderNode>;
  dataSource: FolderMenuDataSource;
  nodeBeingViewed: FolderNode;
  nodeSelected: FolderNode;
  subscriptions: Subscription[] = [];

  constructor(
    injector: Injector,
    private sidebar: SidebarService,
    private changeDetector: ChangeDetectorRef,
    public dragDrop: DragDropService
  ) {
    this.treeControl = new FlatTreeControl<FolderNode>(this.getLevel, this.isExpandable);
    this.dataSource = new FolderMenuDataSource(injector, this.treeControl);
  }

  async ngOnInit() {
    if (!this.isFolderSelect) this.dataSource.loadTopFolder();
    // don't rebuild folder tree for folder select/create menus
    if (this.isFolderSelect) {
      if (this.dataSource.localStorage.folderTree) {
        this.dataSource.data = [this.dataSource.localStorage.folderTree[0]];
        this.dataSource.nodes = this.dataSource.localStorage.folderTree;
        this.changeDetector.detectChanges(); // needed for tree control to respond synchronously
      } else this.dataSource.loadTopFolder(); // load folder tree if it doesn't exist in storage yet (mobile)

      await this.dataSource.setInbox();
      // add inbox node for some folder select menus, don't add if we're moving a folder
      if (this.addInboxNode && !this.foldersToDisable.includes(this.dataSource.inboxNode.id)) {
        this.dataSource.data.unshift(this.dataSource.inboxNode);
        this.selectNode(this.dataSource.inboxNode);
        this.dataSource.expandNode(this.dataSource.data[1]);
      } else {
        this.selectNode(this.dataSource.data[0]);
      }
    }
  }

  ngAfterViewInit() {
    // show folder in menu if on a folder view
    this.subscriptions.push(
      this.sidebar.navigatedToFolder.subscribe((folderId) => {
        // don't manipulate folder select when switching view
        if (this.isFolderSelect) return;

        // load tree control from local storage if reloading app from a folder view
        if (this.dataSource.data.length === 0) {
          this.dataSource.data = [this.dataSource.getRootNode()];
        }
        this.nodeBeingViewed = folderId ? this.dataSource.getNodeById(folderId) : this.dataSource.getRootNode();
        if (!this.nodeBeingViewed) {
          return;
        }

        const parentNode = this.dataSource.expandParentsRecursively(this.nodeBeingViewed);
        // this block needed if we are launching from Create Folder modal
        if (!this.treeControl.dataNodes.find((node) => node && node.id === this.nodeBeingViewed.id)) {
          this.dataSource.addNodeToView(this.nodeBeingViewed, parentNode);
        }
        this.dataSource.expandNode(this.nodeBeingViewed);
      })
    );

    // add folder node to menu after creation
    this.subscriptions.push(
      this.sidebar.createdFolder.subscribe((folder: Folder) => {
        const parentNode = this.dataSource.getNodeById(folder.parent_id);
        const newNode = this.dataSource.addChildNode(folder, parentNode);
        this.dataSource.addNodeToView(newNode, parentNode);
        this.changeDetector.detectChanges();
      })
    );

    // remove folder node from menu after deletion/archival
    this.subscriptions.push(
      this.sidebar.removedFolder.subscribe((folder: Folder) => {
        const parentNode = this.dataSource.getNodeById(folder.parent_id);
        this.dataSource.removeChildNode(folder, parentNode);
        this.dataSource.removeNodeFromView(folder, parentNode);
        this.changeDetector.detectChanges();
      })
    );

    // rename folder node
    this.subscriptions.push(
      this.sidebar.renameFolder.subscribe((folder: Folder) => {
        const folderNode = this.dataSource.getNodeById(folder.id);
        if (folderNode) {
          folderNode.title = folder.title;
          this.dataSource.renameNode(folderNode);
          this.changeDetector.detectChanges();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  getLevel = (node: FolderNode) => (node ? node.level : 1);

  isExpandable = (node: FolderNode) => node && node.children && node.children.length > 0;

  hasChild = (_: number, _nodeData: FolderNode) => _nodeData && _nodeData.children && _nodeData.children.length;

  /**
   * Returns a css class depending on how deeply nested the tree node is
   *
   * @param level the nested level of the MatTreeNode
   */
  nestedLevelClass(level: number): string {
    return !this.isFolderSelect && level > 5 ? 'no-lines nested-5' : `nested-${level}`;
  }

  /**
   * In FolderSelect mode, this is triggered when a node button is clicked
   *
   * @param node The FolderNode that is clicked
   */
  selectNode(node: FolderNode) {
    if (this.nodeSelected) this.nodeSelected.isActive = false;
    this.nodeSelected = node;
    node.isActive = true;
    this.treeControl.expand(node);
    this.folderSelected.emit(node);
  }
}
